import { INCIDENT_TYPE_OPTIONS } from 'features/admin/lib/incidentTypes';

import React, { useEffect, useMemo, useRef } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { i18n } from '@lingui/core';
import { t } from '@lingui/core/macro';

import RefreshButton from 'shared/ui/IconButton/RefreshButton';

import { useGetIncidentTypeStatsQuery } from 'features/admin/api/incidentStatsAPI';
import { getIncidentTypeChartOptions } from 'features/admin/lib/getIncidentTypeChartOptions';

import styles from './IncidentTypesChart.module.scss';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const IncidentTypesChart: React.FC = () => {
    const { data: stats = [], isLoading, isFetching, error, refetch } = useGetIncidentTypeStatsQuery(undefined, {
        pollingInterval: REFRESH_INTERVAL_MS,
    });

    const chartWrapperRef = useRef<HTMLDivElement>(null);
    const hasAutoScrolledRef = useRef(false);

    useEffect(() => {
        if (stats.length > 0 && !hasAutoScrolledRef.current && chartWrapperRef.current) {
            chartWrapperRef.current.scrollLeft = chartWrapperRef.current.scrollWidth;
            hasAutoScrolledRef.current = true;
        }
    }, [stats]);

    const chartData = useMemo(() => ({
        labels: stats.map(item => item.date),
        datasets: INCIDENT_TYPE_OPTIONS.map(option => ({
            label: i18n._(option.label),
            data: stats.map(item => item.incidents[option.id] ?? 0),
            backgroundColor: option.color,
            stack: 'incidents',
            barPercentage: 1,
            categoryPercentage: 1,
        })),
    }), [stats]);

    const chartOptions = useMemo(() => getIncidentTypeChartOptions(stats), [stats]);

    const renderContent = () => {
        if (error) {
            return (
                <div className={styles.error}>
                    <p>{t`Не удалось загрузить данные`}</p>
                    <button onClick={() => refetch()} className={styles.retryButton}>
                        {t`Повторить попытку`}
                    </button>
                </div>
            );
        }

        if (isLoading) {
            return <div className={styles.loading}>{t`Загрузка данных...`}</div>;
        }

        return (
            <div className={styles.chartContainer}>
                <div className={styles.chartWrapper} ref={chartWrapperRef}>
                    <div className={styles.chartCanvasContainer}>
                        <Bar data={chartData} options={chartOptions} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.chartBlock}>
            <div className={styles.header}>
                <h3>{t`Инциденты по типам за последние сутки`}</h3>
                <RefreshButton title={t`Обновить`} onClick={() => refetch()} isRefreshing={isFetching} />
            </div>
            {renderContent()}
        </div>
    );
};

export default IncidentTypesChart;
